/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Route, Routes, useNavigate } from '@thd-olt-component-react/router';
import { Button } from '@one-thd/sui-atomic-components';
import PropTypes from 'prop-types';
import { useStore } from '@thd-nucleus/experience-context';
import { noop } from 'lodash';
import { sizes } from '../../utils/style-utils';
import { getFeatureGates } from '../../utils/featureGates';
import { Utils } from '../../utils/urlUtils';
import { createNewConfigurationStart } from '../../actions/plaConfigurations';
import { getConfigSelections, createConfigId } from '../../api/getPageData';
import { formPlaApiCreatePayload } from '../../utils/plaConfigurationUtils';
import { usePlaConfigurationsParams } from '../../../pages/hooks';
import ProductNotAvailable from '../ProductNotAvailable';
import InfoNotAvailable from '../InfoNotAvailable';
import { trackDesignPurchaseButtonEvent } from '../../utils/analytics/trackEvents';

const ssr = typeof window !== 'undefined';

const doRedirect = async (
  isMobile,
  omsId,
  navigate,
  isStoreEnabled,
  availableSkus,
  selectedSku,
  storeId,
  plaConfiguration,
  config,
  eventName,
  ButtonTitle
) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { plaGuid } = usePlaConfigurationsParams();
  let newConfigurationId;

  if (plaGuid) {
    const getConfigSelectionsResponse = await getConfigSelections(
      availableSkus,
      selectedSku,
      storeId
    );

    const selections = getConfigSelectionsResponse.groups.flatMap((group) =>
      formPlaApiCreatePayload(group.options, plaConfiguration)
    );

    const { additionalDetails, productId, mainImageUrl } = getConfigSelectionsResponse;

    const createConfigIdresponse = await createConfigId(
      productId,
      selections,
      additionalDetails,
      mainImageUrl,
      storeId
    );
    newConfigurationId = createConfigIdresponse.ConfigurationGuid;
  }
  let baseUrl;
  let searchParams;

  if (isStoreEnabled) {
    const env = config.env;
    let params = ssr && new URLSearchParams(window?.location?.search);

    if (Utils.isNucleusProdEnvironment()) {
      baseUrl = '/custom-countertops/countertops/configurator/';
    } else if (Utils.containsNucleusPath()) {
      baseUrl = '/render-nucleus/countertops/configurator/';
    } else {
      // On localhost, may not be needed
      baseUrl = '/countertops/configurator/';
    }

    searchParams = env.search;
    if (newConfigurationId) {
      params.set('configurationIdForSelections', newConfigurationId);
      params.delete('baseConfigurationIdForSelections');
    }
    searchParams = '?' + params.toString();
  } else {
    window.location = isMobile
      ? `https://hdapps.homedepot.com/ApptMakerOnline/custAppt.jsp?deptNbr=${config.thdDepartmentNumber}&prdctTyp=${config.thdProductTypeId}`
      : 'https://hdapps.homedepot.com/CtopEstimator/pages/index.html';
    return;
  }
  trackDesignPurchaseButtonEvent({ eventName, ButtonTitle });
  navigate(`${baseUrl}${omsId}${searchParams}`);
};

const getPromoFootnoteElement = (minimumPromoSqFt, sticky) => {
  if (sticky) {
    return null;
  }

  return (
    <div className="sui-py-3">{`* Order a minimum of ${minimumPromoSqFt} sq. ft. to receive applicable promo pricing.`}</div>
  );
};

const BuildAndBuy = ({
  omsId,
  isProductAvailable,
  minimumPromoSqFt,
  isStoreEnabled,
  contactNumber,
  availableSkus,
  selectedSku,
  plaConfiguration,
  config,
  isProductInfoAvailable,
  history,
  sticky,
  atcRef,
  eventName,
}) => {
  const mediaQueryList = ssr && window?.matchMedia(`(max-width: ${sizes.tablet}px)`);

  const { storeId } = useStore();

  const [isMobile, setIsMobile] = useState(mediaQueryList.matches);

  useEffect(() => {
    const handleMediaChange = (mql) => setIsMobile(mql.matches);
    mediaQueryList.addListener(handleMediaChange);
    return () => {
      mediaQueryList.removeListener(handleMediaChange);
    };
  });

  const enableCecContactNumbers = getFeatureGates()?.enableCecContactNumbers;

  let navigate = useNavigate();

  if (!isProductInfoAvailable) {
    return <InfoNotAvailable history={history} />;
  }

  if (isProductAvailable !== null && !isProductAvailable) {
    return <ProductNotAvailable history={history} />;
  }

  const ButtonTitle = isStoreEnabled || !isMobile ? 'Design and Purchase' : 'Schedule Appointment';

  const component = (
    <>
      {isProductAvailable && (
        <div>
          <div ref={atcRef}>
            <Button
              fullWidth
              onClick={() =>
                doRedirect(
                  isMobile,
                  omsId,
                  navigate,
                  isStoreEnabled,
                  availableSkus,
                  selectedSku,
                  storeId,
                  plaConfiguration,
                  config,
                  eventName,
                  ButtonTitle
                )}
            >
              {ButtonTitle}
            </Button>
          </div>
          {Number(minimumPromoSqFt) > 0 && getPromoFootnoteElement(minimumPromoSqFt, sticky)}
        </div>
      )}
    </>
  );

  if (!ssr) {
    return (
      <div>
        <Button disabled fullWidth onClick={() => noop}>
          {isStoreEnabled || !isMobile ? 'Design and Purchase' : 'Schedule Appointment'}
        </Button>
        {Number(minimumPromoSqFt) > 0 && getPromoFootnoteElement(minimumPromoSqFt)}
      </div>
    );
  }

  return (
    <div>
      <Routes>
        <Route path="/" element={component} />
      </Routes>
    </div>
  );
};

BuildAndBuy.propTypes = {
  isProductAvailable: PropTypes.bool,
  omsId: PropTypes.string,
  minimumPromoSqFt: PropTypes.string,
  isStoreEnabled: PropTypes.bool,
  contactNumber: PropTypes.string,
  availableSkus: PropTypes.string,
  selectedSku: PropTypes.number,
  plaConfiguration: PropTypes.shape({}),
  config: PropTypes.shape({}),
  isProductInfoAvailable: PropTypes.bool,
  sticky: PropTypes.bool.isRequired,
  atcRef: PropTypes.shape({}),
  history: PropTypes.shape({}),
  eventName: PropTypes.string.isRequired,
};

BuildAndBuy.defaultProps = {
  isProductAvailable: false,
  omsId: null,
  contactNumber: null,
  availableSkus: null,
  selectedSku: null,
  plaConfiguration: {},
  config: {},
  history: {},
  minimumPromoSqFt: undefined,
  isStoreEnabled: undefined,
  isProductInfoAvailable: undefined,
  atcRef: undefined,
};

const mapDispatchToProps = (dispatch) => ({
  onCreateNewConfigurationStart: () => dispatch(createNewConfigurationStart()),
});

const mapStateToProps = (state) => ({
  availableSkus: state.countertops.availableSkus,
  selectedSku: state.countertops.selectedSku,
  plaConfiguration: state.plaConfiguration,
});

export default connect(mapStateToProps, mapDispatchToProps)(BuildAndBuy);
